import { useEffect, useState } from 'react';
import { Shield, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import AdminSidebar from '@/components/layout/AdminSidebar';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

export default function UserManager({ user, setUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    // Prevent changing own role
    if (userId === user.user_id) {
      toast.error("You can't change your own role");
      return;
    }

    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole });
      toast.success('Role updated successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-700';
      case 'scorer': return 'bg-blue-100 text-blue-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const adminCount = users.filter(u => u.role === 'admin').length;
  const scorerCount = users.filter(u => u.role === 'scorer').length;
  const viewerCount = users.filter(u => u.role === 'viewer').length;

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminSidebar user={user} setUser={setUser} />
      
      <div className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">User Management</h1>
            <p className="text-slate-500 mt-1">Manage user roles and permissions</p>
          </div>

          {/* Stats */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <Card className="glass-card rounded-2xl">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Admins</p>
                  <p className="font-stats text-2xl text-purple-600">{adminCount}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card rounded-2xl">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Scorers</p>
                  <p className="font-stats text-2xl text-blue-600">{scorerCount}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card rounded-2xl">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Viewers</p>
                  <p className="font-stats text-2xl text-slate-600">{viewerCount}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Users List */}
          <Card className="glass-card rounded-2xl">
            <CardHeader>
              <CardTitle className="text-lg">All Users</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="skeleton h-16 rounded-xl"></div>
                  ))}
                </div>
              ) : users.length > 0 ? (
                <div className="space-y-3">
                  {users.map((u) => (
                    <div 
                      key={u.user_id} 
                      className="flex items-center justify-between p-4 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors"
                      data-testid={`user-${u.user_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={u.profile_image_url} alt={u.name} />
                          <AvatarFallback className="bg-emerald-900 text-white">
                            {u.name?.charAt(0)?.toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-slate-900">
                            {u.name}
                            {u.user_id === user.user_id && (
                              <span className="ml-2 text-xs text-slate-500">(You)</span>
                            )}
                          </p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={getRoleBadgeColor(u.role)}>
                          {u.role.charAt(0).toUpperCase() + u.role.slice(1)}
                        </Badge>
                        <Select 
                          value={u.role} 
                          onValueChange={(value) => handleRoleChange(u.user_id, value)}
                          disabled={u.user_id === user.user_id}
                        >
                          <SelectTrigger className="w-32" data-testid={`role-select-${u.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="scorer">Scorer</SelectItem>
                            <SelectItem value="viewer">Viewer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <p>No users found</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info */}
          <div className="mt-6 p-4 rounded-xl bg-blue-50 border border-blue-100">
            <h4 className="font-medium text-blue-900 mb-2">Role Descriptions</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li><strong>Admin:</strong> Full access to manage leagues, teams, players, matches, and users.</li>
              <li><strong>Scorer:</strong> Can view and score assigned matches.</li>
              <li><strong>Viewer:</strong> Read-only access to public content.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
